import { Plus, Trash2 } from 'lucide-react'

import { FieldError } from '@/shared/components/ui/field-error'

const WARRANTY_OPTIONS = [
  'As per Manufacturer Warranty',
  '1 Year',
  '2 Years',
  '3 Years',
  '4 Years',
  '5 Years',
]

const PAYMENT_TERMS_OPTIONS = [
  'Immediate',
  '7 Days from Invoice Date',
  '15 Days from Invoice Date',
  '21 Days from Invoice Date',
  '30 Days from Invoice Date',
  '45 Days from Invoice Date',
  '60 Days from Invoice Date',
]

const DELIVERY_TERMS_OPTIONS = [
  'Immediate',
  '1 Week',
  '2 Weeks',
  '3 Weeks',
  '4 Weeks',
  '5 Weeks',
  '6 Weeks',
  '7 Weeks',
]

const EXPORT_TAX_TYPES = ['export_lut_igst', 'export_lut_igst_sgst_cgst']

export default function TermsConditionsCard({
  terms,
  errors = {},
  onChange,
  taxType = 'cgst_sgst',
}) {
  const additionalTerms = terms.additionalTerms
    ? String(terms.additionalTerms).split('\n')
    : ['']

  const isCustomWarranty = !WARRANTY_OPTIONS.includes(terms.warranty)

  const isCustomPaymentTerms = !PAYMENT_TERMS_OPTIONS.includes(
    terms.paymentTerms,
  )

  const isCustomDeliveryTerms = !DELIVERY_TERMS_OPTIONS.includes(
    terms.deliveryTerms,
  )

  const handleWarrantyChange = (value) => {
    if (value === '__custom__') {
      onChange('warranty', '')
      return
    }

    onChange('warranty', value)
  }

  const handlePaymentTermsChange = (value) => {
    if (value === '__custom__') {
      onChange('paymentTerms', '')
      return
    }

    onChange('paymentTerms', value)
  }

  const handleDeliveryTermsChange = (value) => {
    if (value === '__custom__') {
      onChange('deliveryTerms', '')
      return
    }

    onChange('deliveryTerms', value)
  }

  const handleAdditionalTermChange = (index, value) => {
    const updatedTerms = [...additionalTerms]
    updatedTerms[index] = value

    onChange('additionalTerms', updatedTerms.join('\n'))
  }

  const handleAddAdditionalTerm = () => {
    onChange('additionalTerms', [...additionalTerms, ''].join('\n'))
  }

  const isExportSupply = EXPORT_TAX_TYPES.includes(taxType)
  const gstApplicabilityText = isExportSupply ? 'No tax applicable.' : ''

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="h-6 w-1 rounded bg-[#1F3FAF]" />

        <h2 className="text-lg font-semibold text-[#1F3FAF]">
          Terms & Conditions
        </h2>
      </div>

      <div className="rounded-xl border border-dashed border-[#9EB5E5] bg-white p-5">
        <div className="grid gap-4">
          {/* Warranty */}
          <div>
            <label className="mb-1 block text-sm font-medium">Warranty</label>

            <select
              value={isCustomWarranty ? '__custom__' : terms.warranty}
              onChange={(e) => handleWarrantyChange(e.target.value)}
              aria-invalid={Boolean(errors['termsAndConditions.warranty'])}
              data-invalid={
                errors['termsAndConditions.warranty'] ? 'true' : undefined
              }
              className="w-full rounded-md border p-2"
            >
              {WARRANTY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}

              <option value="__custom__">Custom</option>
            </select>

            {isCustomWarranty && (
              <input
                type="text"
                value={terms.warranty || ''}
                onChange={(e) => onChange('warranty', e.target.value)}
                placeholder="Enter custom warranty period"
                aria-invalid={Boolean(errors['termsAndConditions.warranty'])}
                data-invalid={
                  errors['termsAndConditions.warranty'] ? 'true' : undefined
                }
                className="mt-2 w-full rounded-md border p-2"
                autoFocus
              />
            )}

            <FieldError message={errors['termsAndConditions.warranty']} />
          </div>

          {/* Payment Terms */}
          <div>
            <label className="mb-1 block text-sm font-medium">
              Payment Terms
            </label>

            <select
              value={isCustomPaymentTerms ? '__custom__' : terms.paymentTerms}
              onChange={(e) => handlePaymentTermsChange(e.target.value)}
              aria-invalid={Boolean(errors['termsAndConditions.paymentTerms'])}
              data-invalid={
                errors['termsAndConditions.paymentTerms'] ? 'true' : undefined
              }
              className="w-full rounded-md border p-2"
            >
              {PAYMENT_TERMS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}

              <option value="__custom__">Custom</option>
            </select>

            {isCustomPaymentTerms && (
              <input
                type="text"
                value={terms.paymentTerms || ''}
                onChange={(e) => onChange('paymentTerms', e.target.value)}
                placeholder="Enter custom payment terms"
                aria-invalid={Boolean(
                  errors['termsAndConditions.paymentTerms'],
                )}
                data-invalid={
                  errors['termsAndConditions.paymentTerms'] ? 'true' : undefined
                }
                className="mt-2 w-full rounded-md border p-2"
                autoFocus
              />
            )}

            <FieldError message={errors['termsAndConditions.paymentTerms']} />
          </div>

          {/* Delivery Terms */}
          <div>
            <label className="mb-1 block text-sm font-medium">
              Delivery Terms
            </label>

            <select
              value={isCustomDeliveryTerms ? '__custom__' : terms.deliveryTerms}
              onChange={(e) => handleDeliveryTermsChange(e.target.value)}
              aria-invalid={Boolean(errors['termsAndConditions.deliveryTerms'])}
              data-invalid={
                errors['termsAndConditions.deliveryTerms'] ? 'true' : undefined
              }
              className="w-full rounded-md border p-2"
            >
              {DELIVERY_TERMS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}

              <option value="__custom__">Custom</option>
            </select>

            {isCustomDeliveryTerms && (
              <input
                type="text"
                value={terms.deliveryTerms || ''}
                onChange={(e) => onChange('deliveryTerms', e.target.value)}
                placeholder="Enter custom delivery terms"
                aria-invalid={Boolean(
                  errors['termsAndConditions.deliveryTerms'],
                )}
                data-invalid={
                  errors['termsAndConditions.deliveryTerms']
                    ? 'true'
                    : undefined
                }
                className="mt-2 w-full rounded-md border p-2"
                autoFocus
              />
            )}

            <FieldError message={errors['termsAndConditions.deliveryTerms']} />
          </div>

          {/* GST Applicability */}
          {isExportSupply && (
            <div>
              <label className="mb-1 block text-sm font-medium">
                GST Applicability
              </label>

              <input
                type="text"
                value={gstApplicabilityText}
                readOnly
                placeholder="No tax applicable."
                className="w-full rounded-md border bg-slate-50 p-2 text-slate-700 outline-none"
              />
            </div>
          )}

          {/* Additional Terms */}
          <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <label className="block text-sm font-medium">
                Additional Terms
              </label>

              <button
                type="button"
                onClick={handleAddAdditionalTerm}
                className="flex shrink-0 items-center gap-2 rounded-full bg-[#111827] px-5 py-2 text-sm font-semibold text-white"
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            </div>

            <div className="space-y-2">
              {additionalTerms.map((term, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={term}
                    onChange={(e) =>
                      handleAdditionalTermChange(index, e.target.value)
                    }
                    placeholder="Enter additional terms"
                    aria-invalid={Boolean(
                      errors['termsAndConditions.additionalTerms'],
                    )}
                    data-invalid={
                      errors['termsAndConditions.additionalTerms']
                        ? 'true'
                        : undefined
                    }
                    className="w-full rounded-md border p-2"
                  />

                  {additionalTerms.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const updatedTerms = additionalTerms.filter(
                          (_, termIndex) => termIndex !== index,
                        )

                        onChange('additionalTerms', updatedTerms.join('\n'))
                      }}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-red-200 text-red-500 transition hover:bg-red-50"
                      title="Delete term"
                      aria-label={`Delete additional term ${index + 1}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <FieldError
              message={errors['termsAndConditions.additionalTerms']}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
